import type { JSX } from 'react';
import { Route, Routes } from 'react-router-dom';
import { FEATURES } from '@yuan-math/config';
import { Layout } from './components/Layout.tsx';
import { RequireLearner } from './components/RequireLearner.tsx';
import { HomePage } from './pages/HomePage.tsx';
import { CoursesPage } from './pages/CoursesPage.tsx';
import { CoursePage } from './pages/CoursePage.tsx';
import { LessonPage } from './pages/LessonPage.tsx';
import { MathMapPage } from './pages/MathMapPage.tsx';
import { ConceptPage } from './pages/ConceptPage.tsx';
import { PracticePage } from './pages/PracticePage.tsx';
import { DashboardPage } from './pages/DashboardPage.tsx';
import { ProgressPage } from './pages/ProgressPage.tsx';
import { AchievementsPage } from './pages/AchievementsPage.tsx';
import { StartPage } from './pages/StartPage.tsx';
import { RegisterPage } from './pages/RegisterPage.tsx';
import { ActivatePage } from './pages/ActivatePage.tsx';
import { SignInPage } from './pages/SignInPage.tsx';
import { AccountPage } from './pages/AccountPage.tsx';
import { ConsentPage } from './pages/ConsentPage.tsx';
import { TeacherPage } from './pages/TeacherPage.tsx';
import { AdminPage } from './pages/AdminPage.tsx';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.tsx';
import { ResetPasswordPage } from './pages/ResetPasswordPage.tsx';
import { DiagnosticPage } from './pages/DiagnosticPage.tsx';
import { PricingPage } from './pages/PricingPage.tsx';
import { AboutPage } from './pages/AboutPage.tsx';
import { ParentPage } from './pages/ParentPage.tsx';
import { PrivacyPage } from './pages/PrivacyPage.tsx';
import { AdventureHomePage } from './pages/adventure/AdventureHomePage.tsx';
import { CharacterPage } from './pages/adventure/CharacterPage.tsx';
import { WorldMapPage } from './pages/adventure/WorldMapPage.tsx';
import { KingdomPage } from './pages/adventure/KingdomPage.tsx';
import { QuestPage } from './pages/adventure/QuestPage.tsx';
import { BossPage } from './pages/adventure/BossPage.tsx';
import { EncounterPage } from './pages/adventure/EncounterPage.tsx';
import { TrialPage } from './pages/adventure/TrialPage.tsx';
import { JournalPage } from './pages/adventure/JournalPage.tsx';
import { NotFoundPage } from './pages/NotFoundPage.tsx';

/**
 * Routing.
 *
 * Public routes are readable without an account — a student should be able to
 * explore the whole Math Map and read a lesson before deciding to sign up.
 * Only routes that *write* to a learner profile are guarded.
 */
export function App(): JSX.Element {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="courses" element={<CoursesPage />} />
        <Route path="courses/:courseId" element={<CoursePage />} />
        <Route path="lessons/:lessonId" element={<LessonPage />} />
        <Route path="map" element={<MathMapPage />} />
        <Route path="concepts/:conceptId" element={<ConceptPage />} />
        <Route path="practice/:conceptId" element={<PracticePage />} />
        <Route path="start" element={<StartPage />} />
        <Route path="register" element={<RegisterPage />} />
        <Route path="activate" element={<ActivatePage />} />
        <Route path="signin" element={<SignInPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="consent" element={<ConsentPage />} />
        <Route path="teach" element={<TeacherPage />} />
        {/* Not wrapped in a guard component. Who may see this is decided by the
            database (`database/admin-schema.sql`), and the page asks it — a
            route-level check here would be a second, weaker answer to the same
            question, and the weaker one is the one that rots. */}
        <Route path="admin" element={<AdminPage />} />
        <Route path="forgot" element={<ForgotPasswordPage />} />
        <Route path="reset" element={<ResetPasswordPage />} />
        <Route path="diagnostic" element={<DiagnosticPage />} />
        {FEATURES.payments ? <Route path="pricing" element={<PricingPage />} /> : null}
        <Route path="about" element={<AboutPage />} />
        <Route path="privacy" element={<PrivacyPage />} />

        <Route
          path="home"
          element={
            <RequireLearner>
              <DashboardPage />
            </RequireLearner>
          }
        />
        <Route
          path="progress"
          element={
            <RequireLearner>
              <ProgressPage />
            </RequireLearner>
          }
        />
        <Route
          path="achievements"
          element={
            <RequireLearner>
              <AchievementsPage />
            </RequireLearner>
          }
        />
        <Route
          path="parent"
          element={
            <RequireLearner>
              <ParentPage />
            </RequireLearner>
          }
        />

        {/* The adventure ships dark (docs/rpg-implementation-plan.md §0.7).
            With the flag off these paths are the 404 they have always been —
            the flag check sits outside RequireLearner so a signed-out visitor
            is told "no such page" rather than sent to sign-up for one. */}
        <Route
          path="adventure"
          element={
            FEATURES.rpg ? (
              <RequireLearner>
                <AdventureHomePage />
              </RequireLearner>
            ) : (
              <NotFoundPage />
            )
          }
        />
        <Route
          path="adventure/character"
          element={
            FEATURES.rpg ? (
              <RequireLearner>
                <CharacterPage />
              </RequireLearner>
            ) : (
              <NotFoundPage />
            )
          }
        />
        <Route
          path="adventure/map"
          element={
            FEATURES.rpg ? (
              <RequireLearner>
                <WorldMapPage />
              </RequireLearner>
            ) : (
              <NotFoundPage />
            )
          }
        />
        <Route
          path="adventure/journal"
          element={
            FEATURES.rpg ? (
              <RequireLearner>
                <JournalPage />
              </RequireLearner>
            ) : (
              <NotFoundPage />
            )
          }
        />
        <Route
          path="adventure/kingdoms/:kingdomId"
          element={
            FEATURES.rpg ? (
              <RequireLearner>
                <KingdomPage />
              </RequireLearner>
            ) : (
              <NotFoundPage />
            )
          }
        />
        <Route
          path="adventure/quests/:questId"
          element={
            FEATURES.rpg ? (
              <RequireLearner>
                <QuestPage />
              </RequireLearner>
            ) : (
              <NotFoundPage />
            )
          }
        />
        <Route
          path="adventure/boss/:bossId"
          element={
            FEATURES.rpg ? (
              <RequireLearner>
                <BossPage />
              </RequireLearner>
            ) : (
              <NotFoundPage />
            )
          }
        />
        {/*
         * The warded site: one concept, read then fought. Behind a learner
         * like every other adventure route, and behind nothing else — the
         * same concept's lesson and `/practice/:conceptId` stay open to a
         * guest, which is the promise this route may not quietly narrow.
         */}
        <Route
          path="adventure/encounter/:conceptId"
          element={
            FEATURES.rpg ? (
              <RequireLearner>
                <EncounterPage />
              </RequireLearner>
            ) : (
              <NotFoundPage />
            )
          }
        />

        {/*
          The trials, at the ends of the two long roads. Behind a learner like
          every other adventure route, and behind nothing else — in particular
          not behind either isle's own achievement, which withholds a keepsake
          and a name and never the mathematics. An unknown id is a 404 rather
          than an empty fight.
        */}
        <Route
          path="adventure/trial/:trialId"
          element={
            FEATURES.rpg ? (
              <RequireLearner>
                <TrialPage />
              </RequireLearner>
            ) : (
              <NotFoundPage />
            )
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
